import { useEffect, useState } from 'react';
import { Button, Divider, Group, SimpleGrid, Switch, TextInput } from '@mantine/core';
import { ErrorAlert } from '@/components/error-alert';
import { api } from '@/lib/api';
import { getErrorMessage } from '@/lib/error-message';
import { CrmColumnFields } from './crm-column-fields';
import type { CrmField } from './crm-column-fields';
import type { CrmColumnConfig } from './crm-config';
import type { CaspioConfig } from './caspio-config';

type CaspioConfigFormProps = {
  config: CaspioConfig;
  onConfigChange: (config: CaspioConfig) => void;
  showCredentials: boolean;
  onShowCredentialsChange: (showCredentials: boolean) => void;
  integrationId?: string;
};

function useCaspioTables(integrationId: string | undefined) {
  const [attempt, setAttempt] = useState(0);
  const [result, setResult] = useState<{ key: string; tables: string[]; error: string | null } | null>(null);
  const key = `${integrationId ?? ''}#${attempt}`;

  useEffect(() => {
    let cancelled = false;

    void api['crm-integrations'].caspio.tables.get({ query: { integrationId } }).then(({ data, error }) => {
      if (cancelled) return;

      setResult(
        data
          ? { key, tables: [...data.tables, ...data.views].sort(), error: null }
          : { key, tables: [], error: getErrorMessage(error, 'Unable to load Caspio tables') },
      );
    });

    return () => {
      cancelled = true;
    };
  }, [integrationId, key]);

  const loaded = result?.key === key ? result : null;

  return {
    tables: loaded?.tables ?? [],
    error: loaded?.error ?? null,
    isLoading: loaded === null,
    retry: () => setAttempt((a) => a + 1),
  };
}

function useCaspioFields(integrationId: string | undefined, tableName: string) {
  const [result, setResult] = useState<{ key: string; fields: CrmField[] } | null>(null);
  const key = `${integrationId ?? ''}#${tableName}`;

  useEffect(() => {
    if (!tableName) return;

    let cancelled = false;

    void api['crm-integrations'].caspio.fields.get({ query: { integrationId, tableName } }).then(({ data }) => {
      if (cancelled) return;

      setResult({ key, fields: data?.fields ?? [] });
    });

    return () => {
      cancelled = true;
    };
  }, [integrationId, tableName, key]);

  const loaded = result?.key === key ? result : null;

  return { fields: tableName ? (loaded?.fields ?? null) : null, isLoading: Boolean(tableName) && loaded === null };
}

export function CaspioConfigForm({
  config,
  onConfigChange,
  showCredentials,
  onShowCredentialsChange,
  integrationId,
}: CaspioConfigFormProps) {
  const { tables, error: tablesError, isLoading: isLoadingTables, retry } = useCaspioTables(integrationId);
  const { fields, isLoading: isLoadingFields } = useCaspioFields(integrationId, config.tableName);

  const replaceColumns = (columns: CrmColumnConfig) => onConfigChange({ ...config, ...columns });

  return (
    <>
      <Divider label="Credentials" labelPosition="left" />

      <Switch
        label="Custom Caspio credentials"
        description="When disabled, uses system-wide credentials"
        checked={showCredentials}
        onChange={(e) => onShowCredentialsChange(e.currentTarget.checked)}
      />

      {showCredentials && (
        <SimpleGrid cols={{ base: 1, sm: 3 }}>
          <TextInput
            label="Account ID"
            placeholder="your-account-id"
            value={config.accountId ?? ''}
            onChange={(e) => onConfigChange({ ...config, accountId: e.currentTarget.value || undefined })}
          />
          <TextInput
            label="Client ID"
            placeholder="your-client-id"
            value={config.clientId ?? ''}
            onChange={(e) => onConfigChange({ ...config, clientId: e.currentTarget.value || undefined })}
          />
          <TextInput
            label="Client Secret"
            type="password"
            placeholder="your-client-secret"
            value={config.clientSecret ?? ''}
            onChange={(e) => onConfigChange({ ...config, clientSecret: e.currentTarget.value || undefined })}
          />
        </SimpleGrid>
      )}

      {tablesError && (
        <Group align="flex-start" gap="sm" wrap="nowrap">
          <div style={{ flex: 1 }}>
            <ErrorAlert message={tablesError} />
          </div>
          <Button variant="subtle" size="compact-sm" onClick={retry}>
            Retry
          </Button>
        </Group>
      )}

      <CrmColumnFields
        config={config}
        onConfigChange={replaceColumns}
        tables={tables}
        fields={fields}
        isLoadingTables={isLoadingTables}
        isLoadingFields={isLoadingFields}
      />
    </>
  );
}
